import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Heart,
  MessageCircle,
  Share2,
  Trash2,
  ImagePlus,
  Video,
  Link as LinkIcon,
  Loader2,
  Plus,
  Flag,
  Smile,
} from 'lucide-react';
import { toast } from 'sonner';
import UserLink from '@/components/UserLink';
import ImageLightbox from '@/components/ImageLightbox';

const REACTIONS = ['👍', '🔥', '😍', '🚀', '😂', '👀', '🎯', '💯'];

function videoEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes('twitch.tv')) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0] === 'videos' && parts[1]) return `https://player.twitch.tv/?video=v${parts[1]}&parent=${window.location.hostname}`;
      if (parts[0]) return `https://player.twitch.tv/?channel=${parts[0]}&parent=${window.location.hostname}`;
    }
    if (u.hostname.includes('vimeo.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0]) return `https://player.vimeo.com/video/${parts[0]}`;
    }
  } catch (_e) { return null; }
  return null;
}

function CreateMediaDialog({ open, onOpenChange, onCreated }) {
  const { t } = useI18n();
  const [kind, setKind] = useState('image');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imagePath, setImagePath] = useState(null);
  const [videoPath, setVideoPath] = useState(null);
  const [externalUrl, setExternalUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setKind('image');
    setTitle('');
    setDescription('');
    setImagePath(null);
    setVideoPath(null);
    setExternalUrl('');
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error(t('media.image_too_large')); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImagePath(data.path);
      toast.success(t('media.upload_ok'));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    finally { setUploading(false); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error(t('media.video_too_large')); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload/video', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setVideoPath(data.path);
      toast.success(t('media.upload_ok'));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error(t('media.title_required')); return; }
    setSubmitting(true);
    try {
      const body = { title: title.trim(), description: description.trim(), kind };
      if (kind === 'image') body.image_path = imagePath;
      if (kind === 'video') body.video_path = videoPath;
      if (kind === 'link') body.external_url = externalUrl.trim();
      const { data } = await api.post('/media', body);
      toast.success(t('media.created'));
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="glass-strong border-white/10 text-white max-w-lg" data-testid="media-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tighter">{t('media.new_post')}</DialogTitle>
          <DialogDescription className="text-white/60">{t('media.new_post_desc')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Kind selector */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'image', icon: ImagePlus, label: t('media.kind.image') },
              { id: 'video', icon: Video, label: t('media.kind.video') },
              { id: 'link', icon: LinkIcon, label: t('media.kind.link') },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                type="button"
                data-testid={`media-kind-${id}`}
                onClick={() => setKind(id)}
                className={`p-3 rounded-xl border text-xs uppercase tracking-wider font-bold transition flex flex-col items-center gap-1.5 ${
                  kind === id
                    ? 'border-[#FF007F] bg-[#FF007F]/10 text-[#FF007F]'
                    : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          <div>
            <Label className="text-white/80">{t('media.field.title')}</Label>
            <Input
              data-testid="media-title-input"
              value={title}
              maxLength={140}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('media.title_placeholder')}
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          <div>
            <Label className="text-white/80">{t('media.field.description')}</Label>
            <Textarea
              data-testid="media-desc-input"
              rows={2}
              value={description}
              maxLength={1000}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('media.desc_placeholder')}
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30 resize-none"
            />
          </div>

          {kind === 'image' && (
            <div>
              <Label className="text-white/80">{t('media.field.upload_image')}</Label>
              <input
                type="file"
                accept="image/*"
                data-testid="media-image-file"
                onChange={(e) => uploadImage(e.target.files?.[0])}
                className="block w-full text-sm text-white/60 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF007F] file:text-white file:font-bold file:cursor-pointer hover:file:bg-[#FF007F]/90"
              />
              {imagePath && (
                <img src={fileUrl(imagePath)} alt="preview" className="mt-2 max-h-40 rounded-lg border border-white/10" />
              )}
            </div>
          )}

          {kind === 'video' && (
            <div>
              <Label className="text-white/80">{t('media.field.upload_video')} <span className="text-[10px] text-white/40">({t('media.video_limit')})</span></Label>
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                data-testid="media-video-file"
                onChange={(e) => uploadVideo(e.target.files?.[0])}
                className="block w-full text-sm text-white/60 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF007F] file:text-white file:font-bold file:cursor-pointer hover:file:bg-[#FF007F]/90"
              />
              {videoPath && (
                <video src={fileUrl(videoPath)} controls className="mt-2 max-h-40 rounded-lg border border-white/10 w-full" />
              )}
            </div>
          )}

          {kind === 'link' && (
            <div>
              <Label className="text-white/80">{t('media.field.external_url')}</Label>
              <Input
                data-testid="media-url-input"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://youtu.be/..."
                className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
              />
              <div className="text-[10px] text-white/40 mt-1">{t('media.url_hint')}</div>
            </div>
          )}

          <Button
            type="submit"
            data-testid="media-submit"
            disabled={
              uploading ||
              submitting ||
              !title.trim() ||
              (kind === 'image' && !imagePath) ||
              (kind === 'video' && !videoPath) ||
              (kind === 'link' && !externalUrl.trim())
            }
            className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide rounded-full"
          >
            {submitting || uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('media.publish')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReactionBar({ media, onReact, onLike }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const reactions = media.reactions || {};
  const viewerReactions = media.viewer_reactions || [];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        data-testid={`media-like-${media.id}`}
        onClick={onLike}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition ${
          media.liked_by_viewer
            ? 'bg-[#FF007F] text-white'
            : 'bg-white/5 hover:bg-white/10 text-white/80'
        }`}
      >
        <Heart className={`w-3.5 h-3.5 ${media.liked_by_viewer ? 'fill-current' : ''}`} /> {media.likes_count}
      </button>
      {Object.entries(reactions).map(([em, count]) => (
        <button
          key={em}
          type="button"
          data-testid={`media-reaction-${media.id}-${em}`}
          onClick={() => onReact(em)}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition ${
            viewerReactions.includes(em)
              ? 'bg-[#00F0FF]/20 border border-[#00F0FF]/40 text-[#00F0FF]'
              : 'bg-white/5 hover:bg-white/10 border border-transparent'
          }`}
        >
          <span>{em}</span>
          <span className="font-bold">{count}</span>
        </button>
      ))}
      <div className="relative">
        <button
          type="button"
          data-testid={`media-reaction-toggle-${media.id}`}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition"
          title={t('media.add_reaction')}
        >
          <Smile className="w-3.5 h-3.5" /> <Plus className="w-3 h-3" />
        </button>
        {open && (
          <div className="absolute z-10 mt-1 left-0 glass-strong border border-white/10 rounded-xl p-2 flex gap-1 shadow-2xl" data-testid={`media-reaction-picker-${media.id}`}>
            {REACTIONS.map((em) => (
              <button
                key={em}
                type="button"
                data-testid={`media-reaction-pick-${media.id}-${em}`}
                onClick={() => { onReact(em); setOpen(false); }}
                className="text-xl hover:scale-125 transition"
              >
                {em}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CommentSection({ media, onCountChange }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/media/${media.id}/comments`).then(({ data }) => setComments(data)).catch(() => {});
  }, [media.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!user || user === false) { toast.message(t('auth.must_login')); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/media/${media.id}/comments`, { text: text.trim() });
      setComments([...comments, data]);
      setText('');
      onCountChange?.((comments.length || 0) + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setSending(false); }
  };

  const delComment = async (cid) => {
    try {
      await api.delete(`/media/comments/${cid}`);
      const next = comments.filter((c) => c.id !== cid);
      setComments(next);
      onCountChange?.(next.length);
    } catch { toast.error(t('common.error')); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5">
      <div className="space-y-2 max-h-48 overflow-y-auto no-scrollbar">
        {comments.length === 0 && (
          <div className="text-[11px] text-white/40 italic">{t('media.no_comments')}</div>
        )}
        {comments.map((c) => (
          <div key={c.id} data-testid={`media-comment-${c.id}`} className="text-xs bg-white/5 rounded-lg p-2 flex gap-2">
            <div className="w-5 h-5 rounded-full bg-[#00F0FF] text-black grid place-items-center text-[10px] font-bold flex-shrink-0">
              {c.author_name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <UserLink userId={c.author_id} name={c.author_name} className="font-semibold text-white/80 text-[11px]" />
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-white/40">{new Date(c.created_at).toLocaleString()}</span>
                  {(user && user !== false && (c.author_id === user.id || user.role === 'admin')) && (
                    <button
                      type="button"
                      onClick={() => delComment(c.id)}
                      className="text-white/30 hover:text-red-300 transition"
                      title={t('common.delete')}
                      data-testid={`media-comment-delete-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="text-white/80 mt-0.5 whitespace-pre-wrap break-words">{c.text}</div>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="mt-2 flex gap-2">
        <Input
          data-testid={`media-comment-input-${media.id}`}
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
          placeholder={user && user !== false ? t('media.comment_placeholder') : t('media.comment_login')}
          disabled={!user || user === false}
          className="bg-black/40 border-white/10 text-white placeholder:text-white/30 text-xs"
        />
        <Button
          type="submit"
          data-testid={`media-comment-submit-${media.id}`}
          disabled={sending || !text.trim() || !user || user === false}
          size="sm"
          className="rounded-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black"
        >
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : t('media.send')}
        </Button>
      </form>
    </div>
  );
}

function MediaCard({ media, viewer, onUpdate, onDelete, lightbox, fromSpot }) {
  const { t } = useI18n();
  const [openComments, setOpenComments] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const like = async () => {
    if (!viewer || viewer === false) { toast.message(t('auth.must_login')); return; }
    if (fromSpot) {
      // Like on spot
      try {
        const { data } = await api.post(`/spots/${media.spot_id}/like`);
        onUpdate({ ...media, likes_count: data.likes_count, liked_by_viewer: data.liked });
      } catch { toast.error(t('common.error')); }
      return;
    }
    try {
      const { data } = await api.post(`/media/${media.id}/like`);
      onUpdate({ ...media, likes_count: data.likes_count, liked_by_viewer: data.liked });
    } catch { toast.error(t('common.error')); }
  };

  const react = async (emoji) => {
    if (!viewer || viewer === false) { toast.message(t('auth.must_login')); return; }
    if (fromSpot) { toast.message(t('media.no_reactions_on_spot')); return; }
    try {
      const { data } = await api.post(`/media/${media.id}/react`, { emoji });
      onUpdate({ ...media, reactions: data.reactions, viewer_reactions: data.viewer_reactions });
    } catch { toast.error(t('common.error')); }
  };

  const share = async () => {
    const url = fromSpot
      ? `${window.location.origin}/maps/fh6#spot=${media.spot_id}`
      : `${window.location.origin}/media#${media.id}`;
    const shareTitle = media.title || 'Horizon.Meet&Cruise';
    // 1) Native share (mobile, modern desktop)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return; // user cancelled, no toast
      }
    }
    // 2) Clipboard API (HTTPS contexts)
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success(t('convoys.copied') || 'Lien copié');
        return;
      }
    } catch (_e) { /* fall through */ }
    // 3) Legacy execCommand fallback (works inside iframes too)
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        toast.success(t('convoys.copied') || 'Lien copié');
        return;
      }
    } catch (_e) { /* fall through */ }
    // 4) Final fallback: show the URL in a prompt the user can copy manually
    window.prompt(t('convoys.copied') || 'Lien à copier', url);
  };

  const del = async () => {
    try {
      await api.delete(`/media/${media.id}`);
      toast.success(t('media.deleted'));
      onDelete(media.id);
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
  };

  const embed = media.kind === 'link' ? videoEmbedUrl(media.external_url) : null;
  const canDelete = viewer && viewer !== false && !fromSpot &&
    (media.author_id === viewer.id || viewer.role === 'admin');

  return (
    <article className="glass rounded-2xl overflow-hidden flex flex-col" data-testid={`media-card-${media.id}`}>
      {/* Visual */}
      <div className="relative bg-black/40 aspect-video">
        {media.kind === 'image' && media.image_path && (
          <button type="button" onClick={() => lightbox(media.image_path)} className="block w-full h-full">
            <img src={fileUrl(media.image_path)} alt={media.title} className="w-full h-full object-cover hover:opacity-90 transition" />
          </button>
        )}
        {media.kind === 'video' && media.video_path && (
          <video src={fileUrl(media.video_path)} controls className="w-full h-full object-contain bg-black" />
        )}
        {media.kind === 'link' && embed && (
          <iframe
            src={embed}
            className="w-full h-full"
            title={media.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
        {media.kind === 'link' && !embed && (
          <a href={media.external_url} target="_blank" rel="noreferrer" className="w-full h-full grid place-items-center text-[#00F0FF] hover:underline">
            <LinkIcon className="w-6 h-6" />
            <span className="ml-2 text-sm">{media.external_url}</span>
          </a>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-bold text-base leading-tight truncate" data-testid={`media-title-${media.id}`}>
              {media.title}
            </h3>
            <div className="text-[11px] text-white/50 mt-0.5">
              <UserLink userId={media.author_id} name={media.author_name} /> · {new Date(media.created_at).toLocaleDateString()}
            </div>
          </div>
          {fromSpot && (
            <Link to={`/maps/fh6`} className="text-[10px] uppercase tracking-wider text-[#00F0FF] hover:underline shrink-0">
              {t('media.from_spot')}
            </Link>
          )}
        </div>
        {media.description && (
          <p className="text-sm text-white/70 mt-2 whitespace-pre-wrap break-words line-clamp-4">{media.description}</p>
        )}

        {/* Interactions */}
        <div className="mt-3 space-y-2">
          {!fromSpot && (
            <ReactionBar media={media} onReact={react} onLike={like} />
          )}
          {fromSpot && (
            <button
              type="button"
              data-testid={`media-like-${media.id}`}
              onClick={like}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition ${
                media.liked_by_viewer
                  ? 'bg-[#FF007F] text-white'
                  : 'bg-white/5 hover:bg-white/10 text-white/80'
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${media.liked_by_viewer ? 'fill-current' : ''}`} /> {media.likes_count}
            </button>
          )}
          <div className="flex items-center gap-2 text-xs text-white/50">
            {!fromSpot && (
              <button
                type="button"
                data-testid={`media-toggle-comments-${media.id}`}
                onClick={() => setOpenComments((v) => !v)}
                className="inline-flex items-center gap-1.5 hover:text-white transition"
              >
                <MessageCircle className="w-3.5 h-3.5" /> {media.comments_count || 0}
              </button>
            )}
            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-1.5 hover:text-[#00F0FF] transition"
              data-testid={`media-share-${media.id}`}
            >
              <Share2 className="w-3.5 h-3.5" /> {t('media.share')}
            </button>
            {viewer && viewer !== false && !fromSpot && media.author_id !== viewer.id && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.post('/reports', { target_id: media.author_id, reason: `media:${media.id}`, context: `media:${media.title}` });
                    toast.success(t('media.reported'));
                  } catch { toast.error(t('common.error')); }
                }}
                className="inline-flex items-center gap-1 hover:text-red-300 transition ml-auto"
                data-testid={`media-report-${media.id}`}
              >
                <Flag className="w-3 h-3" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => setConfirmDel(true)}
                className="inline-flex items-center gap-1 hover:text-red-300 transition"
                data-testid={`media-delete-${media.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
          {openComments && !fromSpot && (
            <CommentSection
              media={media}
              onCountChange={(n) => onUpdate({ ...media, comments_count: n })}
            />
          )}
        </div>
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('media.confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">{media.title}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full bg-white/5 hover:bg-white/10 text-white border-white/10">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); del(); setConfirmDel(false); }}
              className="rounded-full bg-red-500 hover:bg-red-500/90 text-white"
              data-testid={`media-delete-confirm-${media.id}`}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  );
}

export default function Media() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState('posts'); // 'posts' | 'spots'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');
  const [scope, setScope] = useState('all');
  const [kind, setKind] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let kindParam = kind;
      if (tab === 'screenshots') kindParam = 'image';
      else if (tab === 'videos') kindParam = 'videos';
      // Tab is one of: 'posts' | 'screenshots' | 'videos'
      const { data } = await api.get('/media', { params: { sort, scope, kind: kindParam } });
      setItems(data);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [tab, sort, scope, kind]);

  useEffect(() => { load(); }, [load]);

  const updateItem = (updated) => {
    setItems((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 bg-[#0A0A0A] text-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] text-xs uppercase tracking-[0.25em] font-bold">
              <ImagePlus className="w-3 h-3" /> {t('media.label')}
            </div>
            <h1 className="font-display text-4xl lg:text-6xl font-black uppercase tracking-tighter mt-3">
              {t('media.title')}
            </h1>
            <p className="text-white/60 mt-2 max-w-xl">{t('media.subtitle')}</p>
          </div>
          <Button
            data-testid="media-create-btn"
            onClick={() => {
              if (!user || user === false) { toast.message(t('auth.must_login')); return; }
              setCreateOpen(true);
            }}
            className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide h-12 px-6"
          >
            <Plus className="w-4 h-4 mr-2" /> {t('media.new_post')}
          </Button>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-2 border-b border-white/10">
          {[
            { id: 'posts', label: t('media.tab.posts') },
            { id: 'screenshots', label: t('media.tab.screenshots') || 'Screenshots' },
            { id: 'videos', label: t('media.tab.videos') || 'Vidéos' },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              data-testid={`media-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition ${
                tab === id ? 'text-white border-[#FF007F]' : 'text-white/50 border-transparent hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Filters (only for the 'posts' tab — screenshots/videos already filter by kind) */}
        {tab === 'posts' && (
          <div className="mt-4 flex items-center gap-3 flex-wrap text-xs">
            <select
              data-testid="media-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-black/40 border border-white/10 text-white rounded-full px-3 py-1.5"
            >
              <option value="recent">{t('media.sort.recent')}</option>
              <option value="popular">{t('media.sort.popular')}</option>
              <option value="trending">{t('media.sort.trending')}</option>
            </select>
            <select
              data-testid="media-scope-select"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="bg-black/40 border border-white/10 text-white rounded-full px-3 py-1.5"
            >
              <option value="all">{t('media.scope.all')}</option>
              <option value="mine">{t('media.scope.mine')}</option>
              <option value="friends">{t('media.scope.friends')}</option>
            </select>
          </div>
        )}

        {/* Feed */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading && (
            <div className="col-span-full text-center text-white/40 py-12">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> {t('common.loading')}
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="col-span-full glass rounded-2xl p-10 text-center">
              <ImagePlus className="w-8 h-8 mx-auto text-white/30" />
              <div className="text-white/70 font-display font-bold text-lg mt-3">{t('media.empty')}</div>
            </div>
          )}
          {!loading && items.map((m) => (
            <MediaCard
              key={m.id}
              media={m}
              viewer={user}
              onUpdate={updateItem}
              onDelete={removeItem}
              lightbox={setLightboxSrc}
              fromSpot={false}
            />
          ))}
        </div>
      </div>

      <CreateMediaDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(m) => setItems((prev) => [m, ...prev])}
      />
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}
